import { t as tr } from "@/i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowLeftRight, BookOpen, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Download, Eraser, Hand, Loader2, Maximize, PaintBucket, Pencil, RotateCcw, Save, Undo2, Volume2, VolumeX, X, ZoomIn, ZoomOut } from "lucide-react";
import { api, formatApiError, slug } from "@/lib/api";
import { buildLineMask, floodFill } from "@/lib/floodFill";
import { createStroke } from "@/lib/brush";
import { isMuted, setMuted } from "@/lib/sounds";
import { StickerReward } from "@/components/StickerReward";
import { StoryDialog } from "@/components/StoryDialog";
import { ColourWheel, loadCustom } from "@/components/ColourWheel";
import { HueyTip, useHueyTip } from "@/components/HueyTip";
import { isLeftHand, setLeftHand, useLayoutMode } from "@/lib/layoutMode";
import { useAuth } from "@/context/AuthContext";

export const PALETTE = [
  "#FF5252", "#D32F2F", "#FF8A80", "#E91E63", "#F368E0", "#FF9FF3", "#FFC1E3",
  "#FF6B00", "#FF9F43", "#FFB74D", "#FFD166", "#FFEB3B", "#FFF59D",
  "#2E7D32", "#10AC84", "#1DD1A1", "#00C9A7", "#8BC34A", "#C5E1A5",
  "#4ECDC4", "#48DBFB", "#B3E5FC", "#54A0FF", "#4D96FF", "#1E88E5", "#0D47A1",
  "#5F27CD", "#845EC2", "#B388FF", "#E1BEE7",
  "#5C3A21", "#8B5A2B", "#8D5524", "#A1887F", "#C8A27A", "#F5CBA7", "#FFDBAC",
  "#1E293B", "#576574", "#9E9E9E", "#C8D6E5", "#FFFFFF",
];
const MAX_HISTORY = 30;
const ZOOM_MIN = 1, ZOOM_MAX = 6;
const TOOLS = [
  { id: "fill", icon: PaintBucket, label: tr("fill"), color: "bg-[#4ECDC4]" },
  { id: "crayon", icon: Pencil, label: tr("crayon"), color: "bg-[#FFD166]" },
  { id: "eraser", icon: Eraser, label: tr("eraser"), color: "bg-[#FF9FF3]" },
  { id: "move", icon: Hand, label: tr("move"), color: "bg-[#C8D6E5]" },
];
const SIZES = [{ id: "s", r: 0.006, dot: "w-2 h-2" }, { id: "m", r: 0.014, dot: "w-3.5 h-3.5" }, { id: "l", r: 0.028, dot: "w-6 h-6" }];
const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

const ToolButton = ({ onClick, icon: Icon, label, testid, disabled, primary }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className={`btn-pop !px-4 !py-3 min-h-[56px] ${primary ? "btn-red" : "btn-white"}`}>
    <Icon className="w-6 h-6" /> <span className="hidden sm:inline">{label}</span>
  </button>
);

const ToolPicker = ({ tool, setTool, size, setSize, vertical, big }) => (
  <div className={`flex items-center gap-2 shrink-0 ${vertical ? "flex-col" : ""}`} data-testid="tool-picker">
    <div className={`flex items-center bg-[#F3EFE6] p-1 rounded-2xl border-2 border-slate-200 ${vertical ? "flex-col gap-1" : ""}`}>
      {TOOLS.map((t) => (
        <button key={t.id} onClick={() => setTool(t.id)} data-testid={`tool-${t.id}`} aria-pressed={tool === t.id} title={t.label}
          className={`flex items-center justify-center gap-1.5 rounded-xl font-bold transition-colors ${vertical ? "flex-col w-16 h-16 text-[11px] gap-0.5" : big ? "px-5 py-3.5 text-base" : "px-3 sm:px-4 py-2.5 text-sm"} ${tool === t.id ? `${t.color} text-[#1E293B] shadow-[0_3px_0_0_rgba(30,41,59,0.2)]` : "text-slate-500 hover:text-[#1E293B]"}`}>
          <t.icon className={vertical || big ? "w-7 h-7" : "w-5 h-5"} /> <span className={vertical ? "" : "hidden sm:inline"}>{t.label}</span>
        </button>
      ))}
    </div>
    <div className={`flex items-center gap-1 bg-[#F3EFE6] p-1 rounded-2xl border-2 border-slate-200 transition-opacity ${vertical ? "flex-col" : ""} ${tool === "fill" || tool === "move" ? "opacity-40 pointer-events-none" : ""}`} data-testid="brush-size-picker">
      {SIZES.map((s) => (
        <button key={s.id} onClick={() => setSize(s.id)} data-testid={`brush-size-${s.id}`} aria-pressed={size === s.id} title={`Size ${s.id.toUpperCase()}`}
          className={`${vertical || big ? "w-14 h-14" : "w-10 h-10"} rounded-xl flex items-center justify-center transition-colors ${size === s.id ? "bg-white shadow-[0_3px_0_0_rgba(30,41,59,0.2)]" : "hover:bg-white/60"}`}>
          <span className={`${s.dot} rounded-full bg-[#1E293B]`} />
        </button>
      ))}
    </div>
  </div>
);

const Palette = ({ colour, setColour, vertical, big, popSide = "left" }) => {
  const ref = useRef(null);
  const [custom, setCustom] = useState(loadCustom);
  const scrollBy = (d) => ref.current?.scrollBy(vertical ? { top: d, behavior: "smooth" } : { left: d, behavior: "smooth" });
  const sz = vertical || big ? "w-16 h-16" : "w-12 h-12 sm:w-14 sm:h-14";
  const swatch = (c, testid) => (
    <button key={c} onClick={() => setColour(c)} data-testid={testid} aria-label={`Colour ${c}`} aria-pressed={colour === c}
      className={`shrink-0 ${sz} rounded-full border-4 transition-transform duration-150 ${colour === c ? "border-[#1E293B] scale-110 -translate-y-1" : "border-white ring-2 ring-slate-200 hover:scale-105"}`}
      style={{ backgroundColor: c }} />
  );
  if (vertical) {
    return (
      <div className="flex flex-col items-center gap-1 h-full min-h-0" data-testid="side-palette">
        <button onClick={() => scrollBy(-320)} className="shrink-0 w-14 h-10 flex items-center justify-center rounded-xl border-2 border-slate-200 bg-white hover:bg-slate-50" data-testid="palette-scroll-up" aria-label={tr("more_colours_left")}><ChevronUp className="w-6 h-6" /></button>
        <div ref={ref} className="palette-scroll-y flex flex-col items-center gap-3 overflow-y-auto overflow-x-hidden px-3 py-2 flex-1 min-h-0" data-testid="palette-strip">
          <ColourWheel onPick={setColour} custom={custom} setCustom={setCustom} side={popSide} />
          {custom.map((c) => swatch(c, `custom-swatch-${c.slice(1)}`))}
          {custom.length > 0 && <span className="shrink-0 h-0.5 w-10 bg-slate-200 rounded-full my-1" />}
          {PALETTE.map((c) => swatch(c, `palette-swatch-${c.slice(1)}`))}
        </div>
        <button onClick={() => scrollBy(320)} className="shrink-0 w-14 h-10 flex items-center justify-center rounded-xl border-2 border-slate-200 bg-white hover:bg-slate-50" data-testid="palette-scroll-down" aria-label={tr("more_colours_right")}><ChevronDown className="w-6 h-6" /></button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 w-full lg:w-auto lg:flex-1 min-w-0">
      <button onClick={() => scrollBy(-320)} className="hidden sm:flex shrink-0 w-10 h-14 items-center justify-center rounded-xl border-2 border-slate-200 bg-white hover:bg-slate-50" data-testid="palette-scroll-left" aria-label={tr("more_colours_left")}><ChevronLeft className="w-6 h-6" /></button>
      <div ref={ref} className="palette-scroll flex items-center gap-2.5 overflow-x-auto pt-2 pb-2 px-2 flex-1" data-testid="palette-strip">
        <ColourWheel onPick={setColour} custom={custom} setCustom={setCustom} big={big} />
        {custom.map((c) => swatch(c, `custom-swatch-${c.slice(1)}`))}
        {custom.length > 0 && <span className="shrink-0 w-0.5 h-10 bg-slate-200 rounded-full mx-1" />}
        {PALETTE.map((c) => swatch(c, `palette-swatch-${c.slice(1)}`))}
      </div>
      <button onClick={() => scrollBy(320)} className="hidden sm:flex shrink-0 w-10 h-14 items-center justify-center rounded-xl border-2 border-slate-200 bg-white hover:bg-slate-50" data-testid="palette-scroll-right" aria-label={tr("more_colours_right")}><ChevronRight className="w-6 h-6" /></button>
    </div>
  );
};

const HandToggle = ({ leftHand, onToggle }) => (
  <button onClick={onToggle} data-testid="hand-toggle-button" aria-pressed={leftHand} title={leftHand ? tr("left_handed") : tr("right_handed")}
    className="flex flex-col items-center justify-center gap-0.5 w-16 h-16 rounded-2xl border-2 border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-bold text-slate-600">
    <ArrowLeftRight className="w-6 h-6" />
    <span>{leftHand ? tr("left_handed") : tr("right_handed")}</span>
  </button>
);

export default function ColourStudio() {
  const { pageId } = useParams();
  const nav = useNavigate();
  const [hueyTip, dismissHueyTip] = useHueyTip(`/colour/${pageId}`);
  const layout = useLayoutMode();
  const sideLayout = layout === "tablet-landscape";
  const bigUi = layout === "tablet-portrait";
  const [leftHand, setLeftHandState] = useState(isLeftHand);
  const toggleHand = () => { setLeftHand(!leftHand); setLeftHandState(!leftHand); };
  const { user, setUser } = useAuth();
  const firstName = (user?.name || "you").split(" ").filter((p) => !/^(mr|mrs|ms|miss|mx|dr)\.?$/i.test(p))[0] || "you";
  const canvasRef = useRef(null);
  const viewRef = useRef(null);
  const maskRef = useRef(null);
  const originalRef = useRef(null);
  const historyRef = useRef([]);
  const strokeRef = useRef(null);
  const snapshotRef = useRef(null);
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const dragRef = useRef(null);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const [page, setPage] = useState(null);
  const [colour, setColour] = useState(PALETTE[0]);
  const [tool, setTool] = useState("fill");
  const [size, setSize] = useState("m");
  const [zoom, setZoomState] = useState(1);
  const [pan, setPanState] = useState({ x: 0, y: 0 });
  const [historyLen, setHistoryLen] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [reward, setReward] = useState(null);
  const [savedArt, setSavedArt] = useState(null);
  const [storyArt, setStoryArt] = useState(null);
  const [muted, setMutedState] = useState(isMuted);
  const toggleMute = () => { setMuted(!muted); setMutedState(!muted); };
  const closeReward = useCallback(() => setReward(null), []);

  useEffect(() => {
    let url;
    (async () => {
      try {
        const [{ data: p }, img] = await Promise.all([
          api.get(`/pages/${pageId}`),
          api.get(`/pages/${pageId}/image`, { responseType: "blob" }),
        ]);
        setPage(p);
        url = URL.createObjectURL(img.data);
        const el = new Image();
        el.onload = () => {
          const c = canvasRef.current;
          c.width = el.naturalWidth;
          c.height = el.naturalHeight;
          const ctx = c.getContext("2d", { willReadFrequently: true });
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(el, 0, 0);
          originalRef.current = ctx.getImageData(0, 0, c.width, c.height);
          maskRef.current = buildLineMask(originalRef.current);
        };
        el.src = url;
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
    return () => url && URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per page; other refs are stable
  }, [pageId]);

  // ----- zoom & pan -----
  const clampPan = (p, z) => {
    const c = canvasRef.current, v = viewRef.current;
    if (!c || !v || z <= 1) return { x: 0, y: 0 };
    const maxX = Math.max(0, (c.offsetWidth * z - v.clientWidth) / 2) + 24;
    const maxY = Math.max(0, (c.offsetHeight * z - v.clientHeight) / 2) + 24;
    return { x: Math.min(maxX, Math.max(-maxX, p.x)), y: Math.min(maxY, Math.max(-maxY, p.y)) };
  };
  const setView = (z, p) => {
    zoomRef.current = z;
    panRef.current = clampPan(p, z);
    setZoomState(z);
    setPanState(panRef.current);
  };
  const viewCenter = () => {
    const r = viewRef.current.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };
  const zoomAt = (nz, sOld, sNew, fromZoom = zoomRef.current, fromPan = panRef.current) => {
    nz = clampZoom(nz);
    const c = viewCenter();
    const px = (sOld.x - c.x - fromPan.x) / fromZoom, py = (sOld.y - c.y - fromPan.y) / fromZoom;
    setView(nz, { x: sNew.x - c.x - nz * px, y: sNew.y - c.y - nz * py });
  };
  const zoomStep = (dir) => {
    const nz = clampZoom(Math.round((zoomRef.current + dir * 0.5) * 2) / 2);
    const f = nz / zoomRef.current;
    setView(nz, { x: panRef.current.x * f, y: panRef.current.y * f });
  };
  const resetView = () => setView(1, { x: 0, y: 0 });

  useEffect(() => {
    const v = viewRef.current;
    if (!v) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * 0.0015);
      const pt = { x: e.clientX, y: e.clientY };
      zoomAt(zoomRef.current * factor, pt, pt);
    };
    v.addEventListener("wheel", onWheel, { passive: false });
    return () => v.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- painting -----
  const pushHistory = (snapshot) => {
    historyRef.current = [...historyRef.current.slice(-(MAX_HISTORY - 1)), snapshot];
    setHistoryLen(historyRef.current.length);
  };
  const canvasXY = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    return [((e.clientX - rect.left) * c.width) / rect.width, ((e.clientY - rect.top) * c.height) / rect.height];
  };
  const insideCanvas = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  };
  const cancelStroke = () => {
    if (!strokeRef.current) return;
    canvasRef.current.getContext("2d").putImageData(snapshotRef.current, 0, 0);
    strokeRef.current = null;
    snapshotRef.current = null;
  };

  const onDown = (e) => {
    const c = canvasRef.current;
    if (!c || !maskRef.current) return;
    if (e.target !== c && e.target !== viewRef.current) return;
    viewRef.current.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      cancelStroke();
      dragRef.current = null;
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, zoom: zoomRef.current, pan: { ...panRef.current } };
      return;
    }
    if (tool === "move" || e.button === 1) {
      dragRef.current = { x: e.clientX, y: e.clientY, pan: { ...panRef.current } };
      return;
    }
    if (!insideCanvas(e)) return;
    const [x, y] = canvasXY(e);
    const ctx = c.getContext("2d", { willReadFrequently: true });
    const snapshot = ctx.getImageData(0, 0, c.width, c.height);
    if (tool === "fill") {
      if (floodFill(ctx, maskRef.current, Math.floor(x), Math.floor(y), colour)) pushHistory(snapshot);
      return;
    }
    snapshotRef.current = snapshot;
    const radius = SIZES.find((s) => s.id === size).r * c.width;
    strokeRef.current = createStroke(ctx, maskRef.current, tool === "eraser" ? null : colour, radius, originalRef.current.data);
    strokeRef.current.to(x, y);
  };

  const onMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinchRef.current && pointersRef.current.size >= 2) {
      const [a, b] = [...pointersRef.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const s = pinchRef.current;
      zoomAt(s.zoom * (dist / Math.max(1, s.dist)), s.mid, mid, s.zoom, s.pan);
      return;
    }
    if (dragRef.current) {
      const d = dragRef.current;
      setView(zoomRef.current, { x: d.pan.x + (e.clientX - d.x), y: d.pan.y + (e.clientY - d.y) });
      return;
    }
    if (strokeRef.current) {
      const [x, y] = canvasXY(e);
      strokeRef.current.to(x, y);
    }
  };

  const onUp = (e) => {
    pointersRef.current.delete(e.pointerId);
    if (pinchRef.current) {
      if (pointersRef.current.size < 2) pinchRef.current = null;
      return;
    }
    if (dragRef.current) { dragRef.current = null; return; }
    if (!strokeRef.current) return;
    strokeRef.current = null;
    pushHistory(snapshotRef.current);
    snapshotRef.current = null;
  };

  const undo = () => {
    const prev = historyRef.current.pop();
    if (prev) canvasRef.current.getContext("2d").putImageData(prev, 0, 0);
    setHistoryLen(historyRef.current.length);
  };

  const reset = () => {
    if (!originalRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    pushHistory(ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height));
    ctx.putImageData(originalRef.current, 0, 0);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/pages/${pageId}/artworks`, { image_base64: canvasRef.current.toDataURL("image/png") });
      setUser((u) => (u ? { ...u, stickers: data.stickers, badges: data.badges } : u));
      setReward({ id: data.sticker, isNew: data.is_new, milestone: data.milestone, at: Date.now() });
      setSavedArt(data.artwork);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = canvasRef.current.toDataURL("image/png");
    a.download = `${slug(page?.title)}-coloured.png`;
    a.click();
  };

  const cursor = tool === "move" ? (dragRef.current ? "cursor-grabbing" : "cursor-grab") : "canvas-cursor";

  return (
    <div className="h-[100dvh] flex flex-col bg-[#FFF9F2] relative" data-testid="colour-studio" data-layout={layout} data-hand={leftHand ? "left" : "right"}>
      <StickerReward reward={reward} onClose={closeReward} />
      <StoryDialog art={storyArt} artist={firstName} onClose={() => setStoryArt(null)} />
      <HueyTip tip={hueyTip} onDismiss={dismissHueyTip} className={`fixed top-36 z-40 ${sideLayout && !leftHand ? "right-32" : "right-3"}`} />
      {savedArt && !reward && !storyArt && (
        <div className="absolute left-1/2 -translate-x-1/2 top-24 z-40 rise" data-testid="story-offer">
          <div className="flex items-center gap-3 bg-white rounded-3xl border-4 border-[#1E293B] shadow-[6px_8px_0_0_#1E293B] pl-4 pr-2 py-2">
            <span className="w-11 h-11 rounded-2xl bg-[#FFD166] border-2 border-[#1E293B] flex items-center justify-center shrink-0"><BookOpen className="w-6 h-6" /></span>
            <div className="min-w-0">
              <p className="font-display font-semibold leading-tight">{tr("saved_want_a_bedtime_story_about_it")}</p>
              <p className="text-xs text-slate-500">Written just for {firstName} — read aloud or print it.</p>
            </div>
            <button onClick={() => setStoryArt(savedArt)} className="btn-pop btn-red !py-2.5 !px-4 text-sm shrink-0" data-testid="story-offer-write-button">{tr("write_my_story")}</button>
            <button onClick={() => setSavedArt(null)} className="p-2 rounded-xl hover:bg-slate-100" aria-label={tr("keep_colouring")} data-testid="story-offer-dismiss-button"><X className="w-5 h-5" /></button>
          </div>
        </div>
      )}
      <div className="h-20 px-3 sm:px-6 bg-white border-b-2 border-slate-200 flex items-center justify-between gap-3 shadow-sm">
        <button onClick={() => nav("/colour")} data-testid="studio-back-button" className="btn-pop btn-white !px-4 !py-3 min-h-[56px]">
          <ArrowLeft className="w-6 h-6" /> <span className="hidden sm:inline">{tr("back")}</span>
        </button>
        <h1 className="font-display text-xl sm:text-2xl font-semibold truncate" data-testid="studio-page-title">{page?.title || "Loading..."}</h1>
        <div className="flex items-center gap-2">
          <button onClick={toggleMute} data-testid="sound-toggle-button" aria-pressed={muted} title={muted ? "Sounds off" : "Sounds on"}
            className="btn-pop btn-white !px-3 !py-3 min-h-[56px]">
            {muted ? <VolumeX className="w-6 h-6 text-slate-400" /> : <Volume2 className="w-6 h-6" />}
          </button>
          <ToolButton onClick={undo} icon={Undo2} label={tr("undo")} testid="undo-button" disabled={!historyLen} />
          <ToolButton onClick={reset} icon={RotateCcw} label={tr("reset")} testid="reset-button" />
          <ToolButton onClick={download} icon={Download} label={tr("download")} testid="download-artwork-button" />
          <ToolButton onClick={save} icon={saving ? Loader2 : Save} label={tr("save")} testid="save-artwork-button" disabled={saving} primary />
        </div>
      </div>
      <div className={`flex-1 min-h-0 flex ${sideLayout ? (leftHand ? "flex-row-reverse" : "flex-row") : "flex-col"}`}>
        {sideLayout && (
          <aside className="shrink-0 flex flex-col items-center justify-between gap-3 px-2 py-3 bg-white border-slate-200 border-e-2 shadow-sm" data-testid="tool-rail">
            <ToolPicker tool={tool} setTool={setTool} size={size} setSize={setSize} vertical />
            <HandToggle leftHand={leftHand} onToggle={toggleHand} />
          </aside>
        )}
        <div ref={viewRef} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
          className={`relative flex-1 min-h-0 min-w-0 flex items-center justify-center p-4 sm:p-6 dot-grid overflow-hidden select-none ${cursor}`}
          style={{ touchAction: "none" }} data-testid="canvas-viewport">
          {error ? (
            <p className="font-bold text-red-600" data-testid="studio-error">{error}</p>
          ) : (
            <canvas ref={canvasRef} data-testid="colour-canvas" data-tool={tool} data-zoom={zoom}
              className="max-w-full max-h-full bg-white rounded-2xl border-4 border-[#1E293B] shadow-[6px_8px_0_0_#1E293B]"
              style={{ imageRendering: "auto", transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "center", transition: pinchRef.current || dragRef.current ? "none" : "transform 80ms ease-out" }} />
          )}
          <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/95 backdrop-blur rounded-2xl border-2 border-slate-200 p-1 shadow-md" data-testid="zoom-controls">
            <button onClick={() => zoomStep(-1)} disabled={zoom <= ZOOM_MIN} data-testid="zoom-out-button" aria-label={tr("zoom_out")} className="w-11 h-11 rounded-xl flex items-center justify-center hover:bg-slate-100 disabled:opacity-30"><ZoomOut className="w-6 h-6" /></button>
            <span className="w-14 text-center font-bold text-sm tabular-nums" data-testid="zoom-level">{Math.round(zoom * 100)}%</span>
            <button onClick={() => zoomStep(1)} disabled={zoom >= ZOOM_MAX} data-testid="zoom-in-button" aria-label={tr("zoom_in")} className="w-11 h-11 rounded-xl flex items-center justify-center hover:bg-slate-100 disabled:opacity-30"><ZoomIn className="w-6 h-6" /></button>
            <button onClick={resetView} disabled={zoom === 1} data-testid="zoom-reset-button" aria-label={tr("fit_to_screen")} title={tr("fit_to_screen")} className="w-11 h-11 rounded-xl flex items-center justify-center hover:bg-slate-100 disabled:opacity-30"><Maximize className="w-5 h-5" /></button>
          </div>
          {zoom > 1 && (
            <p className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs font-bold text-slate-600 bg-white/90 rounded-full px-3 py-1 border border-slate-200 pointer-events-none" data-testid="zoom-hint">
              {tr("pinch_or_two_fingers_to_move_or_pick_the")}
            </p>
          )}
        </div>
        {sideLayout ? (
          <aside className="shrink-0 w-28 bg-white border-slate-200 border-s-2 shadow-sm py-2 min-h-0" data-testid="colour-palette">
            <Palette colour={colour} setColour={setColour} vertical popSide={leftHand ? "right" : "left"} />
          </aside>
        ) : (
          <div className="px-3 py-3 bg-white border-t-2 border-slate-200 shadow-lg" data-testid="colour-palette">
            <div className="flex flex-col lg:flex-row items-center gap-3 lg:gap-4">
              <ToolPicker tool={tool} setTool={setTool} size={size} setSize={setSize} big={bigUi} />
              <Palette colour={colour} setColour={setColour} big={bigUi} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
